import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import {
  Instagram, Youtube, MapPin, IndianRupee, Award, ExternalLink,
  ArrowLeft, ShieldCheck, AlertTriangle, BarChart3, Loader2, Plus, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const fmtInr = (n) => (n || n === 0 ? `₹${Number(n).toLocaleString('en-IN')}` : '—');

export const CreatorProfileBrand = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  const [brandsWorkedWith, setBrandsWorkedWith] = useState([]);
  const [suitabilityTags, setSuitabilityTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shortlists, setShortlists] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: profile }, { data: sls }] = await Promise.all([
          axios.get(`${API_URL}/api/creators/${id}/profile`, { withCredentials: true }),
          axios.get(`${API_URL}/api/shortlists`, { withCredentials: true }),
        ]);
        if (!cancelled) {
          // Backend returns { creator, stats, brands_worked_with, history, suitability_tags }
          setCreator(profile?.creator || null);
          setProfileStats(profile?.stats || null);
          setBrandsWorkedWith(Array.isArray(profile?.brands_worked_with) ? profile.brands_worked_with : []);
          setSuitabilityTags(Array.isArray(profile?.suitability_tags) ? profile.suitability_tags : []);
          setShortlists(Array.isArray(sls) ? sls : []);
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to load creator');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const saveToShortlist = async (shortlistId) => {
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/shortlists/${shortlistId}/creators`,
        { creator_ids: [id] },
        { withCredentials: true }
      );
      toast.success('Saved to shortlist');
      const { data: sls } = await axios.get(`${API_URL}/api/shortlists`, { withCredentials: true });
      setShortlists(sls || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const igUrl = creator?.instagram_handle
    ? `https://instagram.com/${creator.instagram_handle.replace(/^@/, '')}`
    : creator?.instagram_link || null;

  // Platform presence: "ig_only" (default) | "yt_only" | "ig_and_yt".
  // Legacy creators without the field default to ig_only if they have an IG handle.
  const platformPresence = creator?.platform_presence
    || (creator?.instagram_handle ? 'ig_only' : (creator?.youtube_link ? 'yt_only' : 'ig_only'));
  const isYtOnly = platformPresence === 'yt_only';
  const isIgAndYt = platformPresence === 'ig_and_yt';
  const ytUrl = creator?.youtube_link || null;
  const ytIsRealUrl = !!(ytUrl && /^https?:\/\//i.test(ytUrl));

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
          <Loader2 className="animate-spin text-[#FF451A]" size={32} />
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400">Creator not found.</div>
      </div>
    );
  }

  const savedInShortlists = shortlists.filter((s) => (s.creator_ids || []).includes(id));

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-creator-profile">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6"
            data-testid="profile-back-button"
          >
            <ArrowLeft size={14} /> Back
          </button>

          {/* Header card */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-8 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-6 flex-1 min-w-0">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-3xl font-bold shrink-0">
                  {creator.name?.[0]?.toUpperCase() || 'C'}
                </div>
                <div className="flex-1 min-w-0">
                <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="profile-name">
                  {creator.name}
                </h1>
                {isYtOnly ? (
                  <span
                    className="text-lg text-[#FF0000] inline-flex items-center gap-1.5"
                    data-testid="profile-yt-inline"
                  >
                    <Youtube size={18} /> YouTube Creator
                  </span>
                ) : creator.instagram_handle ? (
                  <a
                    href={igUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-lg text-zinc-300 hover:text-[#E1306C] inline-flex items-center gap-1.5 transition-colors"
                    data-testid="profile-ig-inline"
                  >
                    <Instagram size={16} /> @{creator.instagram_handle}
                  </a>
                ) : null}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(creator.categories || []).map((cat) => (
                    <span key={cat} className="px-2.5 py-0.5 text-xs rounded-full bg-[#FF451A]/10 text-[#FF451A] font-medium uppercase tracking-wider">
                      {cat}
                    </span>
                  ))}
                  {(creator.cities || []).slice(0, 3).map((city) => (
                    <span key={city} className="px-2.5 py-0.5 text-xs rounded-full bg-white/5 text-zinc-300 font-medium flex items-center gap-1">
                      <MapPin size={10} /> {city}
                    </span>
                  ))}
                  {creator.exclusivity_badge?.label && (() => {
                    const colorKey = (creator.exclusivity_badge.color || 'grey').toLowerCase();
                    const classes = {
                      green:  'bg-[#00C972]/15 text-[#00C972] border-[#00C972]/30',
                      orange: 'bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/30',
                      blue:   'bg-[#0055FF]/15 text-[#0055FF] border-[#0055FF]/30',
                      grey:   'bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10',
                    }[colorKey] || 'bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10';
                    return (
                      <span
                        className={`px-2.5 py-0.5 text-xs rounded-full font-semibold uppercase tracking-wider border ${classes}`}
                        data-testid="profile-exclusivity-badge"
                      >
                        {creator.exclusivity_badge.label}
                      </span>
                    );
                  })()}
                  <span
                    className={`px-2.5 py-0.5 text-xs rounded-full font-semibold flex items-center gap-1 ${
                      creator.exclusivity_type === 'exclusive' ? 'bg-white/10 text-[#0F0F0F] dark:text-white' : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 border border-black/[0.10] dark:border-white/10'
                    }`}
                  >
                    {creator.exclusivity_type === 'exclusive' ? <Award size={10} /> : null}
                    {creator.exclusivity_type === 'exclusive' ? 'Managed' : 'Marketplace'}
                  </span>
                  {isYtOnly && (
                    <span
                      className="px-2.5 py-0.5 text-xs rounded-full font-semibold flex items-center gap-1 bg-[#FF0000]/15 text-[#FF0000] border border-[#FF0000]/30"
                      data-testid="profile-yt-exclusive-badge"
                    >
                      <Youtube size={10} /> YouTube Exclusive
                    </span>
                  )}
                  {isIgAndYt && (
                    <span
                      className="px-2.5 py-0.5 text-xs rounded-full font-semibold flex items-center gap-1 bg-white/5 text-[#0F0F0F] dark:text-white border border-black/[0.10] dark:border-white/10"
                      data-testid="profile-igyt-badge"
                    >
                      <Instagram size={10} className="text-[#E1306C]" />
                      <Youtube size={11} className="text-[#FF0000]" />
                      Instagram + YouTube
                    </span>
                  )}
                </div>
              </div>
              </div>

              {/* Right column: Instagram / YouTube CTAs + shortlist */}
              <div className="flex flex-col gap-2 w-full lg:w-auto lg:flex-shrink-0 lg:min-w-[260px]">
                {isYtOnly ? (
                  ytIsRealUrl ? (
                    <>
                      <a
                        href={ytUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white rounded-full px-5 py-2.5 font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-[0_4px_20px_rgba(255,0,0,0.25)] w-full lg:w-auto"
                        data-testid="view-youtube-button"
                      >
                        <Youtube size={16} /> View YouTube Channel
                      </a>
                      <a
                        href={ytUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-5 py-2.5 font-medium text-sm flex items-center justify-center gap-2 transition-colors w-full lg:w-auto"
                        data-testid="open-youtube-newtab"
                      >
                        Open YouTube in new tab <ExternalLink size={13} />
                      </a>
                    </>
                  ) : (
                    <div
                      className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 rounded-full px-5 py-2.5 font-medium text-sm flex items-center justify-center gap-2 w-full lg:w-auto"
                      data-testid="yt-link-pending"
                    >
                      <Youtube size={14} className="text-[#FF0000]" /> YouTube link coming soon
                    </div>
                  )
                ) : (
                  <>
                    {igUrl && (
                      <a
                        href={igUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-gradient-to-r from-[#E1306C] to-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-[0_4px_20px_rgba(225,48,108,0.25)] w-full lg:w-auto"
                        data-testid="view-instagram-button"
                      >
                        <Instagram size={16} /> View Instagram
                      </a>
                    )}
                    {igUrl && (
                      <a
                        href={igUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-5 py-2.5 font-medium text-sm flex items-center justify-center gap-2 transition-colors w-full lg:w-auto"
                        data-testid="open-instagram-newtab"
                      >
                        Open Instagram in new tab <ExternalLink size={13} />
                      </a>
                    )}
                  </>
                )}

                {/* Save to shortlist */}
                {shortlists.length > 0 ? (
                  <SaveToShortlistMenu
                    shortlists={shortlists}
                    savedInIds={savedInShortlists.map((s) => s.id)}
                    onSave={saveToShortlist}
                    disabled={busy}
                  />
                ) : (
                  <Link
                    to="/brand/shortlists"
                    className="border border-black/[0.10] dark:border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white rounded-full px-5 py-2.5 font-medium text-sm flex items-center justify-center gap-2 transition-colors w-full lg:w-auto"
                    data-testid="create-first-shortlist-link"
                  >
                    <Plus size={14} /> Create first shortlist
                  </Link>
                )}

                {savedInShortlists.length > 0 && (
                  <div className="text-[10px] uppercase tracking-wider text-[#00C972] flex items-center gap-1 mt-1" data-testid="saved-in-shortlists">
                    <Check size={11} /> In {savedInShortlists.length} shortlist{savedInShortlists.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats grid — platform-aware */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {isYtOnly ? (
              <>
                <StatTile icon={Youtube} iconColor="#FF0000" label="YT Subscribers" value={fmtNum(creator.youtube_subscribers)} testid="stat-yt-subs" />
                <StatTile icon={IndianRupee} iconColor="#00C972" label="Short Price" value={fmtInr(creator.youtube_pricing?.short)} testid="stat-yt-short" />
                <StatTile icon={IndianRupee} iconColor="#00C972" label="Integrated" value={fmtInr(creator.youtube_pricing?.integrated)} testid="stat-yt-integrated" />
                <StatTile icon={IndianRupee} iconColor="#00C972" label="Dedicated" value={fmtInr(creator.youtube_pricing?.dedicated)} testid="stat-yt-dedicated" />
              </>
            ) : (
              <>
                <StatTile icon={Instagram} iconColor="#E1306C" label="IG Followers" value={fmtNum(creator.instagram_followers)} testid="stat-followers" />
                <StatTile icon={BarChart3} iconColor="#FFB800" label="Engagement" value={`${(creator.instagram_engagement || 0).toFixed(2)}%`} testid="stat-engagement" />
                <StatTile icon={Youtube} iconColor="#FF0000" label="YT Subs" value={fmtNum(creator.youtube_subscribers)} testid="stat-yt-subs" />
                <StatTile icon={IndianRupee} iconColor="#00C972" label="Reel Price" value={fmtInr(creator.pricing_reel)} testid="stat-reel-price" />
              </>
            )}
          </div>

          {/* Pricing + Quality + Audience trust */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <SectionCard title="Pricing" testid="profile-pricing-card">
              {isYtOnly ? (
                <>
                  <Row label="YT Short" value={fmtInr(creator.youtube_pricing?.short)} icon={IndianRupee} />
                  <Row label="Integrated" value={fmtInr(creator.youtube_pricing?.integrated)} icon={IndianRupee} />
                  <Row label="Dedicated" value={fmtInr(creator.youtube_pricing?.dedicated)} icon={IndianRupee} />
                  {creator.youtube_pricing?.boosting > 0 && (
                    <Row label="Boosting (1mo)" value={fmtInr(creator.youtube_pricing?.boosting)} icon={IndianRupee} />
                  )}
                </>
              ) : (
                <>
                  {isIgAndYt && (
                    <div className="text-[10px] uppercase tracking-wider text-[#E1306C] font-semibold mb-1 flex items-center gap-1">
                      <Instagram size={10} /> Instagram
                    </div>
                  )}
                  <Row label="Reel" value={fmtInr(creator.pricing_reel)} icon={IndianRupee} />
                  <Row label="Story" value={fmtInr(creator.pricing_story)} icon={IndianRupee} />
                  {creator.cpv != null && creator.cpv > 0 && (
                    <Row label="CPV (cost per view)" value={`₹${Number(creator.cpv).toFixed(2)}`} />
                  )}
                  {isIgAndYt && creator.youtube_pricing && (
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-[#FF0000] font-semibold mt-3 mb-1 flex items-center gap-1">
                        <Youtube size={11} /> YouTube
                      </div>
                      <Row label="Short" value={fmtInr(creator.youtube_pricing?.short)} icon={IndianRupee} />
                      <Row label="Integrated" value={fmtInr(creator.youtube_pricing?.integrated)} icon={IndianRupee} />
                      <Row label="Dedicated" value={fmtInr(creator.youtube_pricing?.dedicated)} icon={IndianRupee} />
                    </>
                  )}
                </>
              )}
            </SectionCard>

            <SectionCard title="Trust & Quality" testid="profile-quality-card">
              <Row
                label="Quality grade"
                value={
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold tracking-wider bg-white/5 border border-black/[0.10] dark:border-white/10">
                    <Award size={11} /> {creator.quality_grade || '—'}
                  </span>
                }
              />
              <Row
                label="Brand safety"
                value={
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold ${
                    (creator.brand_safety_score || 0) >= 90 ? 'bg-[#00C972]/15 text-[#00C972]' :
                    (creator.brand_safety_score || 0) >= 70 ? 'bg-[#FFB800]/15 text-[#FFB800]' :
                    'bg-[#FF3B30]/15 text-[#FF3B30]'
                  }`}>
                    <ShieldCheck size={11} /> {creator.brand_safety_score ?? '—'}
                  </span>
                }
              />
              <Row
                label="Fake follower score"
                value={
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold ${
                    (creator.fake_follower_score || 0) <= 10 ? 'bg-[#00C972]/15 text-[#00C972]' :
                    (creator.fake_follower_score || 0) <= 25 ? 'bg-[#FFB800]/15 text-[#FFB800]' :
                    'bg-[#FF3B30]/15 text-[#FF3B30]'
                  }`}>
                    <AlertTriangle size={11} /> {creator.fake_follower_score ?? '—'}%
                  </span>
                }
              />
            </SectionCard>
          </div>

          {/* Reach + Languages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <SectionCard title="Reach" testid="profile-reach-card">
              {!isYtOnly && (
                <Row
                  label="Instagram"
                  value={
                    igUrl ? (
                      <a href={igUrl} target="_blank" rel="noreferrer" className="text-[#E1306C] hover:underline inline-flex items-center gap-1" data-testid="profile-ig-link">
                        @{creator.instagram_handle} <ExternalLink size={11} />
                      </a>
                    ) : '—'
                  }
                  icon={Instagram}
                />
              )}
              {creator.youtube_link && (
                <Row
                  label={isYtOnly ? 'YouTube Channel' : 'YouTube'}
                  value={
                    ytIsRealUrl ? (
                      <a href={creator.youtube_link} target="_blank" rel="noreferrer" className="text-[#FF0000] hover:underline inline-flex items-center gap-1 truncate" data-testid="profile-yt-link">
                        {isYtOnly ? 'Open channel' : 'Channel'} <ExternalLink size={11} />
                      </a>
                    ) : (
                      <span className="text-[#6B6B6B] dark:text-zinc-500 italic text-xs" data-testid="profile-yt-pending">
                        Link pending
                      </span>
                    )
                  }
                  icon={Youtube}
                />
              )}
              {isYtOnly && creator.youtube_subscribers > 0 && (
                <Row label="Subscribers" value={fmtNum(creator.youtube_subscribers)} icon={Youtube} />
              )}
            </SectionCard>

            <SectionCard title="Languages" testid="profile-languages-card">
              {(creator.languages || []).length === 0 ? (
                <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">Not provided</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {(creator.languages || []).map((l) => (
                    <span key={l} className="px-2.5 py-0.5 text-xs rounded-full bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 font-medium">
                      {l}
                    </span>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          {/* Audience Insights — surfaces audience_age_group + top_3_cities (public) */}
          {(creator.audience_age_group || creator.top_3_cities || creator.female_ratio != null) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-testid="profile-audience-insights">
              <SectionCard title="Audience Insights" testid="profile-audience-card">
                {creator.audience_age_group && (
                  <Row label="Audience Age" value={creator.audience_age_group} />
                )}
                {creator.female_ratio != null && creator.female_ratio !== '' && (
                  <Row label="Female Ratio" value={`${creator.female_ratio}%`} />
                )}
                {creator.top_3_cities && (
                  <Row label="Top Cities" value={creator.top_3_cities} />
                )}
              </SectionCard>
              {creator.foc_deliverables && (
                <SectionCard title="FOC Deliverables" testid="profile-foc-card">
                  <div className="text-sm text-[#0F0F0F] dark:text-white">{creator.foc_deliverables}</div>
                </SectionCard>
              )}
            </div>
          )}

          {/* Track record — only show if there's any history at all */}
          {(profileStats?.total_campaigns > 0 || brandsWorkedWith.length > 0 || suitabilityTags.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="profile-track-record">
              <SectionCard title="Track record" testid="profile-trackrecord-card">
                <Row label="Total campaigns" value={profileStats?.total_campaigns ?? 0} />
                <Row label="Locked / completed" value={profileStats?.locked_or_completed ?? 0} />
                <Row
                  label="Acceptance rate"
                  value={`${profileStats?.acceptance_rate ?? 0}%`}
                />
                <Row label="Total earnings" value={fmtInr(profileStats?.total_earnings)} />
                {brandsWorkedWith.length > 0 && (
                  <div className="pt-2 border-t border-black/[0.08] dark:border-white/5">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1.5 font-mono">Brands worked with</div>
                    <div className="flex flex-wrap gap-1.5">
                      {brandsWorkedWith.slice(0, 12).map((b) => (
                        <span key={b} className="px-2 py-0.5 text-xs rounded-full bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 font-medium">
                          {b}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </SectionCard>

              {suitabilityTags.length > 0 && (
                <SectionCard title="Best fit for" testid="profile-suitability-card">
                  <div className="flex flex-wrap gap-1.5">
                    {suitabilityTags.map((t) => (
                      <span key={t} className="px-2.5 py-0.5 text-xs rounded-full bg-[#0055FF]/10 text-[#0055FF] border border-[#0055FF]/20 font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                </SectionCard>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatTile = ({ icon: Icon, iconColor, label, value, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1.5 font-mono">
      <Icon size={11} style={{ color: iconColor }} /> {label}
    </div>
    <div className="text-2xl font-bold text-[#0F0F0F] dark:text-white">{value}</div>
  </div>
);

const SectionCard = ({ title, children, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-4 font-mono">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Row = ({ label, value, icon: Icon }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="text-sm text-[#6B6B6B] dark:text-zinc-400 flex items-center gap-1.5">
      {Icon && <Icon size={12} className="text-[#6B6B6B] dark:text-zinc-500" />} {label}
    </div>
    <div className="text-sm text-[#0F0F0F] dark:text-white font-medium">{value}</div>
  </div>
);

const SaveToShortlistMenu = ({ shortlists, savedInIds, onSave, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="w-full bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#E63E17] flex items-center justify-center gap-2 transition-colors disabled:opacity-40"
        data-testid="save-to-shortlist-button"
      >
        <Plus size={14} /> Save to shortlist
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 rounded-xl shadow-2xl p-2 z-10" data-testid="shortlist-menu">
          {shortlists.map((s) => {
            const saved = savedInIds.includes(s.id);
            return (
              <button
                key={s.id}
                disabled={saved || disabled}
                onClick={async () => { await onSave(s.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 transition-colors ${
                  saved ? 'text-[#6B6B6B] dark:text-zinc-500 bg-white/[0.02]' : 'text-[#0F0F0F] dark:text-white hover:bg-white/5'
                }`}
                data-testid={`shortlist-option-${s.id}`}
              >
                <span className="truncate">{s.name}</span>
                {saved ? <Check size={13} className="text-[#00C972]" /> : <span className="text-[10px] text-[#6B6B6B] dark:text-zinc-500">{s.creator_count}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
